import { forkJoin, map, Observable } from 'rxjs';
import {Size} from "../models/utils";

/**
 * creates a not visible image tag so that the browser preloads all given images
 *
 * @param urls image urls to preload
 * @returns an observable that emits false at first
 * after all given image urls are loaded from browser the observable will emit true
 * to indicate that all images are preloaded by the browser
 */
export const preloadImages = (urls: string[]): Observable<boolean> => {
  const listOfPromises = [];
  for (const url of urls) {
    const imagePromise = new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = '*';

      const lastEventListener = () => {
        img?.remove();
        resolve();
      };

      img.addEventListener('load', lastEventListener);
      img.onerror = err => {
        console.warn('Error during loading of image', url, err);
        reject(err);
      };
      img.src = url;
    });
    listOfPromises.push(imagePromise);
  }

  return forkJoin(listOfPromises).pipe(map(() => true));
};


export const loadImage = async (image: { url?: string, base64?: string, resizeTo?: Size }): Promise<HTMLImageElement> => {
  if (image.url) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = '*';

      img.onload = () => resolve(img);
      img.onerror = reject
      img.src = image.url!;
    });
  }

  if (image.base64) {
    let base64Image = image.base64;
    if (image.resizeTo){
      base64Image = await resizeBase64Image(image.base64!, image.resizeTo)
    }

    return base64ToImage(base64Image);
  }
  throw new Error('No image url or base64 given');
};

export async function resizeBase64Image(base64: string, { width, height }: Size): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = `data:image/png;base64,${base64.replace(/^.+,/, '')}`
    img.crossOrigin = '*';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height);

      return resolve(canvas.toDataURL('image/jpeg'))
    };
    img.onerror = error => {
      reject(error);
    };
  })

}

async function base64ToImage(base64Image: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>(async (resolve, reject) => {
    const image = new Image();
    image.src = `data:image/png;base64,${base64Image.replace(/^.+,/, '')}`
    image.onload = () => resolve(image);
    image.onerror = error => reject(error);
  });
}
